import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GOOGLE_MAPS_API_KEY } from "../../config/googleMaps.js";
import { loadGoogleMapsLibrary } from "../../utils/loadGoogleMaps.js";

const GERMANY_CENTER = { lat: 51.1657, lng: 10.4515 };

function buildInfoContent(f, lang, t) {
  const wrapper = document.createElement("div");
  wrapper.style.cssText = "font-family:inherit; max-width:220px;";
  const title = document.createElement("strong");
  title.textContent = f.title;
  const location = document.createElement("div");
  location.style.cssText = "font-size:13px; margin:4px 0; color:#333;";
  location.textContent = f.location;
  const link = document.createElement("a");
  link.href = `/${lang}/events/${f.slug}`;
  link.style.cssText = "font-size:13px;";
  link.textContent = t("events.detailsCta");
  wrapper.append(title, location, link);
  return wrapper;
}

/**
 * Only ever mounted when the user actively switches to the "Karte" view
 * (see EventList.jsx) — never on page load or the default List view. The
 * Maps JavaScript API bills per map load past a monthly free quota, so
 * this on-demand-only loading is the main cost control; see
 * konzepte/GOOGLE_MAPS_SETUP.md for the full picture (API key
 * restrictions, budget alert, quota cap).
 */
export default function EventsMap({ events, lang }) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !containerRef.current) return undefined;
    let cancelled = false;
    let markers = [];

    // Google calls this global on key/referrer/billing problems — a
    // separate mechanism from a rejected promise, so the try/catch below
    // alone doesn't always catch it (the script can "load" successfully
    // and only fail once it actually authenticates).
    window.gm_authFailure = () => {
      console.error(
        "EventsMap: Google Maps auth failure — check API key restrictions (HTTP referrer, API restriction) and that billing is enabled, see konzepte/GOOGLE_MAPS_SETUP.md"
      );
      if (!cancelled) setError(true);
    };

    (async () => {
      try {
        const [{ Map, InfoWindow }, { LatLngBounds }, { Marker }] = await Promise.all([
          loadGoogleMapsLibrary("maps"),
          loadGoogleMapsLibrary("core"),
          loadGoogleMapsLibrary("marker"),
        ]);
        if (cancelled || !containerRef.current) return;

        const map = new Map(containerRef.current, {
          center: GERMANY_CENTER,
          zoom: 6,
          streetViewControl: false,
          mapTypeControl: false,
        });

        const withCoords = events.filter((e) => e.frontmatter.coordinates?.length === 2);
        if (withCoords.length === 0) return;

        const infoWindow = new InfoWindow();
        const bounds = new LatLngBounds();

        markers = withCoords.map((event) => {
          const f = event.frontmatter;
          const position = { lat: f.coordinates[0], lng: f.coordinates[1] };
          bounds.extend(position);
          const marker = new Marker({ map, position, title: f.title });
          marker.addListener("click", () => {
            infoWindow.setContent(buildInfoContent(f, lang, t));
            infoWindow.open({ map, anchor: marker });
          });
          return marker;
        });

        if (withCoords.length === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(11);
        } else {
          map.fitBounds(bounds, 48);
        }
      } catch (err) {
        // Google Maps almost always logs a specific typed error itself
        // (RefererNotAllowedMapError / ApiNotActivatedMapError /
        // BillingNotEnabledMapError / InvalidKeyMapError, etc.) — surface
        // ours too so the real cause shows up in devtools instead of just
        // the generic fallback text.
        console.error("EventsMap: failed to load Google Maps", err);
        if (!cancelled) setError(true);
      }
    })();

    return () => {
      cancelled = true;
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [events, lang, t]);

  if (!GOOGLE_MAPS_API_KEY) {
    return <p style={{ fontSize: "14px", color: "var(--color-cream-soft)" }}>{t("events.mapNotConfigured")}</p>;
  }
  if (error) {
    return <p style={{ fontSize: "14px", color: "var(--color-cream-soft)" }}>{t("events.mapError")}</p>;
  }

  return <div ref={containerRef} style={{ width: "100%", height: "520px", borderRadius: "14px" }} />;
}
